import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../features/vibe-coding/redux/actions';
import {
  VIBE_DEFAULT_BLUEPRINT,
  VIBE_DEFAULT_MODEL_ID,
  getDefaultThinkingForModel,
  resolveThinkingForModel,
} from '../../features/vibe-coding/redux/constants';
import CommonPageComponent from '../../common/ui/CommonPageComponent';
import BuildProgress from './components/BuildProgress';
import ChatInput from './components/ChatInput';
import { getErrorMessage, getShortChatError } from './vibeCodingUtils';

export class InitialPromptPage extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object,
    projectId: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    vibe: PropTypes.object,
  };

  state = {
    prompt: '',
    attachments: [],
    isFocused: false,
    isGenerating: false,
    submittedPrompt: '',
    submittedAttachments: [],
    selectedModel: VIBE_DEFAULT_MODEL_ID,
    selectedThinking: getDefaultThinkingForModel(VIBE_DEFAULT_MODEL_ID),
  };

  isUnmounted = false;

  textareaRef = createRef();

  getQueryParams = () => {
    const search =
      (this.props.location && this.props.location.search) ||
      (typeof window !== 'undefined' ? window.location.search : '') ||
      '';
    const params = new URLSearchParams(search);
    return {
      prompt: params.get('prompt') || '',
      keys: params.get('keys') || [],
    };
  };

  componentDidMount() {
    const { actions, projectId, version } = this.props;
    if (actions && actions.clearConversationHistory && projectId && version) {
      actions.clearConversationHistory({ projectId, version });
    }

    const { prompt, keys: keysParam } = this.getQueryParams();

    const keys = Array.isArray(keysParam)
      ? keysParam
      : keysParam
      ? keysParam.split(',').filter(Boolean)
      : [];

    const attachments = keys.map((key) => ({
      key,
      name: key.split('/').pop(),
    }));

    this.setState({
      prompt,
      attachments,
    });

    if (prompt || attachments.length > 0) {
      this.handleSubmit(prompt, attachments);
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    const { vibe, actions: vibeActions } = this.props;
    if (vibe && vibe.generateVibeResponsePending) {
      vibeActions.stopVibeGeneration({ silent: true });
    }
  }

  handlePromptChange = (event) => {
    const value = event && event.target ? event.target.value : '';
    this.setState({ prompt: value });
  };

  handleAttachmentsChange = (attachments) => {
    this.setState({ attachments });
  };

  handleModelChange = (modelId) => {
    if (!modelId) return;
    this.setState((prev) => ({
      selectedModel: modelId,
      selectedThinking: resolveThinkingForModel(modelId, prev.selectedThinking),
    }));
  };

  handleThinkingChange = (thinkingId) => {
    if (!thinkingId) return;
    this.setState({ selectedThinking: thinkingId });
  };

  buildModelPayload = () => ({
    model: this.state.selectedModel,
    thinking: this.state.selectedThinking,
  });

  handleFocus = () => this.setState({ isFocused: true });

  handleBlur = () => this.setState({ isFocused: false });

  handleCardClick = () => {
    if (this.textareaRef.current) {
      this.textareaRef.current.focus();
    }
  };

  handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.state.isGenerating) return;
      this.handleSubmit();
    }
  };

  handleStop = () => {
    const { actions: vibeActions } = this.props;
    if (vibeActions && vibeActions.stopVibeGeneration) {
      vibeActions.stopVibeGeneration();
    }
  };

  handleSubmit = async (submitPrompt = null, submitAttachments = null) => {
    const { prompt: statePrompt, attachments: stateAttachments, isGenerating } = this.state;

    const promptToSubmit = submitPrompt !== null ? submitPrompt : statePrompt;
    const attachmentsToSubmit =
      submitAttachments !== null && submitAttachments.length > 0
        ? submitAttachments
        : stateAttachments;

    const trimmed = (promptToSubmit || '').trim();
    const promptText =
      trimmed ||
      (attachmentsToSubmit && attachmentsToSubmit.length > 0
        ? 'Please build an application based on the attached files.'
        : '');

    if (!promptText || isGenerating) return;

    const { actions: vibeActions, history, projectId, version } = this.props;
    if (!history || !projectId || !version) return;

    const conversationId = uuidv4();
    this.setState({
      isGenerating: true,
      prompt: '',
      attachments: [],
      submittedPrompt: promptText,
      submittedAttachments: attachmentsToSubmit,
    });

    const result = await vibeActions.generateVibeResponseStream({
      projectId,
      version,
      data: {
        prompt: promptText,
        attachments: attachmentsToSubmit,
        conversationId,
        blueprint: VIBE_DEFAULT_BLUEPRINT,
        isInitialRequest: true,
        ...this.buildModelPayload(),
      },
    });

    if (this.isUnmounted) return;

    if (!result || result.ok === false) {
      toast.error(getShortChatError(getErrorMessage(result && result.error, '')));
      this.setState({
        isGenerating: false,
        prompt: trimmed,
        attachments: attachmentsToSubmit,
      });
      return;
    }

    if (result.stopped) {
      this.setState({ isGenerating: false });
      return;
    }

    history.push(`/2.0/vibe-coding/${version}/v/${projectId}/chat/${conversationId}`);
  };

  render() {
    const {
      prompt,
      isFocused,
      isGenerating,
      attachments,
      submittedPrompt,
      submittedAttachments,
      selectedModel,
      selectedThinking,
    } = this.state;
    const vibe = this.props.vibe || {};

    const userMsg = (vibe.messages || []).filter((m) => m.role === 'user').pop();
    const userMessage = (userMsg && userMsg.content) || submittedPrompt || '';
    const userAttachments =
      userMsg && Array.isArray(userMsg.attachments) && userMsg.attachments.length > 0
        ? userMsg.attachments
        : submittedAttachments || [];

    return (
      <CommonPageComponent>
        <div
          className="d-flex flex-column align-items-center justify-content-start"
          style={{
            minHeight: 'calc(100vh - 120px)',
            height: isGenerating ? 'calc(100vh - 120px)' : 'auto',
            maxHeight: isGenerating ? 'calc(100vh - 120px)' : 'none',
            width: '100%',
            margin: '0 auto',
            overflow: isGenerating ? 'hidden' : 'visible',
            paddingTop: isGenerating ? '10px' : '40px',
          }}
        >
          <div
            style={{
              maxWidth: '960px',
              width: '100%',
              height: isGenerating ? '100%' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {!isGenerating && (
              <div className="text-center mb-4 flex-shrink-0">
                <h2
                  className="font-weight-bold text-dark mb-2"
                  style={{ fontSize: '32px', letterSpacing: '-0.5px' }}
                >
                  What would you like to build?
                </h2>
                <p
                  className="text-muted mx-auto mb-0"
                  style={{ fontSize: '15px', maxWidth: '540px', lineHeight: '1.5' }}
                >
                  Describe your idea in simple words, and we&apos;ll build your application for you.
                </p>
              </div>
            )}

            {isGenerating && (
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', marginBottom: '20px' }}>
                <BuildProgress
                  milestones={vibe.milestones}
                  phase={vibe.currentPhase}
                  userMessage={userMessage}
                  attachments={userAttachments}
                  streamingMessage={vibe.streamingMessage}
                  isRunning={isGenerating}
                />
              </div>
            )}

            <div className="flex-shrink-0" style={{ marginBottom: '10px' }}>
              <ChatInput
                textareaRef={this.textareaRef}
                prompt={prompt}
                isFocused={isFocused}
                canSubmit={
                  (Boolean(prompt.trim()) ||
                    (Array.isArray(attachments) && attachments.length > 0)) &&
                  !isGenerating
                }
                isGenerating={isGenerating}
                selectedModel={selectedModel}
                selectedThinking={selectedThinking}
                onModelChange={this.handleModelChange}
                onThinkingChange={this.handleThinkingChange}
                onChange={this.handlePromptChange}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onKeyDown={this.handleKeyDown}
                onCardClick={this.handleCardClick}
                onSubmit={this.handleSubmit}
                onStop={this.handleStop}
                attachments={attachments}
                onAttachmentsChange={this.handleAttachmentsChange}
              />
            </div>
          </div>
        </div>
      </CommonPageComponent>
    );
  }
}

function mapStateToProps(state) {
  return {
    vibe: state.vibeCoding,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(InitialPromptPage);
